import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./JobManagement.css";
import { createJob, getJobs } from "../services/jobService";

const initialFormState = {
  job_code: "",
  title: "",
  description: "",
  location: "",
  experience: "",
  employment_type: "FULL_TIME",
  is_active: true,
};

const isJobActive = (job) => job?.is_active === true || job?.is_active === "true";
const JOBS_PAGE_LIMIT = 100;

const extractJobsFromResponse = (data) => {
  if (Array.isArray(data)) {
    return data;
  }
  if (Array.isArray(data?.data)) {
    return data.data;
  }
  if (Array.isArray(data?.content)) {
    return data.content;
  }
  if (Array.isArray(data?.jobs)) {
    return data.jobs;
  }
  return [];
};

const JobManagement = () => {
  const adminBasePath = "/components/Admins/AdminLayout";
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [formData, setFormData] = useState(initialFormState);
  const [loading, setLoading] = useState(false);

  const summaryLabel = useMemo(() => {
    const activeCount = jobs.filter(isJobActive).length;
    return `${jobs.length} postings | ${activeCount} active`;
  }, [jobs]);

  // ✅ FIXED FETCH FUNCTION
//  const fetchJobs = async () => {
//   try {
//     const res = await getJobs();
//     console.log("Jobs API:", res);
 
//     setJobs(res || []);
//   } catch (err) {
//     console.error("Error fetching jobs:", err);
//   }
// };


  const fetchJobs = async () => {
    try {
      const firstPage = await getJobs({ page: 1, limit: JOBS_PAGE_LIMIT });
      console.log("All Jobs Response:", firstPage);

      const firstJobs = extractJobsFromResponse(firstPage);
      const detectedTotalPages = Number(
        firstPage?.totalPages ??
        firstPage?.data?.totalPages ??
        1
      );

      if (!Number.isFinite(detectedTotalPages) || detectedTotalPages <= 1) {
        setJobs(firstJobs);
        return;
      }

      const remainingResponses = await Promise.all(
        Array.from({ length: detectedTotalPages - 1 }, (_, index) =>
          getJobs({ page: index + 2, limit: JOBS_PAGE_LIMIT })
        )
      );

      const combinedJobs = [
        ...firstJobs,
        ...remainingResponses.flatMap(extractJobsFromResponse),
      ];

      setJobs(combinedJobs);
    } catch (error) {
      console.error("Failed to load jobs", error);
      setJobs([]);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const resetForm = () => {
    setFormData(initialFormState);
  };

  // 🚀 CREATE JOB (UNCHANGED)
  const handleSubmit = async () => {
    if (
      !formData.job_code ||
      !formData.title ||
      !formData.location ||
      !formData.description
    ) {
      alert("Please fill required fields");
      return;
    }

    const payload = {
      job_code: formData.job_code,
      title: formData.title,
      description: formData.description,
      location: formData.location,
      experience: formData.experience
        ? parseInt(formData.experience, 10)
        : 0,
      employment_type: formData.employment_type,
      is_active: formData.is_active,
    };

    try {
      setLoading(true);

      await createJob(payload);

      await fetchJobs();

      resetForm();

      alert("Job created successfully");

    } 
        catch (error) {
      console.error("Error creating job:", error);

      if (error.message === "Job code already exists") {
        alert("Job code already exists");
      } else {
        alert(error.message || "Failed to create job");
      }
    }
 
  
    finally {
      setLoading(false);
    }
  };

  console.log('this is jobs',jobs)

  return (
    <div className="job-container">
      <header className="job-header">
        <div>
          <h2 className="job-title">Job Management</h2>
          <p className="job-subtitle">
            Maintain openings and posting visibility.
          </p>
          <span className="job-meta-pill">{summaryLabel}</span>
        </div>

        <button className="job-reset-btn" onClick={resetForm}>
          Clear Form
        </button>
      </header>

      <div className="job-layout">

        {/* FORM */}
        <section className="job-form-card">
          <h3>Add Job Posting</h3>

          <input
            type="text"
            name="job_code"
            placeholder="Job Code"
            value={formData.job_code}
            onChange={handleChange}
          />

          <input
            type="text"
            name="title"
            placeholder="Title"
            value={formData.title}
            onChange={handleChange}
          />

          <div className="job-row">
            <input
              type="text"
              name="location"
              placeholder="Location"
              value={formData.location}
              onChange={handleChange}
            />

            <input
              type="number"
              name="experience"
              placeholder="Experience"
              value={formData.experience}
              onChange={handleChange}
            />
          </div>

          <select
            name="employment_type"
            value={formData.employment_type}
            onChange={handleChange}
          >
            <option value="FULL_TIME">Full-time</option>
            <option value="PART_TIME">Part-time</option>
            <option value="CONTRACT">Contract</option>
            <option value="INTERN">Internship</option>
          </select>

          <textarea
            name="description"
            placeholder="Description"
            value={formData.description}
            onChange={handleChange}
          />

          <label className="active-toggle">
            is_active
            <input
              type="checkbox"
              name="is_active"
              checked={formData.is_active}
              onChange={handleChange}
            />
          </label>

          <button
            className="job-save-btn"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? "Creating..." : "Create Posting"}
          </button>
        </section>

        {/* JOB LIST */}
        <section className="job-list-card">
          <div className="job-list-head-row">
            <h3
              role="button"
              tabIndex={0}
              className="all-jobs-link-title"
            >
              All Postings
            </h3>

            <button
              className="job-reset-btn"
              onClick={() => navigate(`${adminBasePath}/all-posts`)}
            >
              Click to All Posts
            </button>
          </div>

          <div className="job-list">
            {jobs.map((job) => (
              <article key={job.id || job.job_code} className="job-card">
                <div className="job-card-top">
                  <h4>
                    {job.job_code} - {job.title}
                  </h4>

                  <div className="job-badges">
                    <span className="job-status">
                      {job.employment_type}
                    </span>

                    <span
                      className={`job-active ${
                        isJobActive(job) ? "active" : "inactive"
                      }`}
                    >
                      {isJobActive(job) ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>

                <p className="job-meta">Location: {job.location}</p>
                <p className="job-meta">
                  Experience: {job.experience || "Not set"}
                </p>
                <p className="job-desc">
                  {job.description || "No description added."}
                </p>
              </article>
            ))}

            {jobs.length === 0 && (
              <div className="job-empty-state">
                No job postings available.
              </div>
            )}
          </div>
        </section>

      </div>
    </div>
  );
};

export default JobManagement;
